import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Sparkles, ChevronDown } from 'lucide-react';
import { Input } from '../SubscriptionAppInput';
import { Button } from '../SubscriptionAppButton';
interface AutomationRule {
  id: string;
  event: string;
  eventValue?: string | number;
  condition?: {
    field: string;
    operator: string;
    value: string | number;
  };
  action: 'apply' | 'remove';
}
interface EditTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tagData: {
    label: string;
    color: string;
    rules: AutomationRule[];
  }) => void;
  onDelete?: () => void;
  tag: {
    id: string;
    label: string;
    color: string;
    rules?: AutomationRule[];
  };
}
const COLOR_PRESETS = [{
  name: 'Sky Blue',
  value: '#0EA5E9'
}, {
  name: 'Green',
  value: '#10B981'
}, {
  name: 'Red',
  value: '#EF4444'
}, {
  name: 'Amber',
  value: '#F59E0B'
}, {
  name: 'Purple',
  value: '#8B5CF6'
}, {
  name: 'Pink',
  value: '#EC4899'
}, {
  name: 'Indigo',
  value: '#6366F1'
}, {
  name: 'Gray',
  value: '#64748B'
}, {
  name: 'Teal',
  value: '#14B8A6'
}, {
  name: 'Orange',
  value: '#F97316'
}];
const EVENT_OPTIONS = [{
  group: 'Subscription Events',
  options: [{
    value: 'subscription_created',
    label: 'Subscription created'
  }, {
    value: 'subscription_cancelled',
    label: 'Subscription cancelled'
  }, {
    value: 'subscription_paused',
    label: 'Subscription paused'
  }, {
    value: 'subscription_resumed',
    label: 'Subscription resumed'
  }, {
    value: 'subscription_on_hold',
    label: 'Subscription on hold'
  }, {
    value: 'orders_completed',
    label: 'Orders completed =',
    needsValue: true,
    valueType: 'number'
  }, {
    value: 'member_for_months',
    label: 'Member for',
    needsValue: true,
    valueType: 'number',
    valueSuffix: 'months'
  }]
}, {
  group: 'Payment Events',
  options: [{
    value: 'payment_failed',
    label: 'Payment failed'
  }, {
    value: 'payment_recovered',
    label: 'Payment recovered'
  }, {
    value: 'payment_method_updated',
    label: 'Payment method updated'
  }, {
    value: 'payment_method_expired',
    label: 'Payment method expired'
  }]
}, {
  group: 'Engagement Events',
  options: [{
    value: 'no_activity_days',
    label: 'No activity for',
    needsValue: true,
    valueType: 'number',
    valueSuffix: 'days'
  }, {
    value: 'orders_skipped',
    label: 'Orders skipped =',
    needsValue: true,
    valueType: 'number'
  }]
}, {
  group: 'Value Events',
  options: [{
    value: 'ltv_reaches',
    label: 'LTV reaches',
    needsValue: true,
    valueType: 'currency'
  }, {
    value: 'aov_above',
    label: 'AOV above',
    needsValue: true,
    valueType: 'currency'
  }]
}];
const CONDITION_FIELDS = [{
  value: 'retry_count',
  label: 'Retry count'
}, {
  value: 'subscription_status',
  label: 'Subscription status'
}, {
  value: 'days_since_last_order',
  label: 'Days since last order'
}, {
  value: 'total_orders',
  label: 'Total orders'
}];
const CONDITION_OPERATORS = [{
  value: 'equals',
  label: 'equals'
}, {
  value: 'greater_than',
  label: 'greater than'
}, {
  value: 'less_than',
  label: 'less than'
}, {
  value: 'gte',
  label: '≥'
}, {
  value: 'lte',
  label: '≤'
}];
export function EditTagModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  tag
}: EditTagModalProps) {
  const [tagLabel, setTagLabel] = useState(tag.label);
  const [tagColor, setTagColor] = useState(tag.color);
  const [rules, setRules] = useState<AutomationRule[]>(tag.rules || []);
  const [showExamples, setShowExamples] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setTagLabel(tag.label);
      setTagColor(tag.color);
      setRules(tag.rules || []);
    }
  }, [isOpen, tag]);
  if (!isOpen) return null;
  const handleAddRule = () => {
    const newRule: AutomationRule = {
      id: `rule-${Date.now()}`,
      event: '',
      action: 'apply'
    };
    setRules([...rules, newRule]);
  };
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(r => r.id !== ruleId));
  };
  const handleUpdateRule = (ruleId: string, updates: Partial<AutomationRule>) => {
    setRules(rules.map(r => r.id === ruleId ? {
      ...r,
      ...updates
    } : r));
  };
  const handleAddCondition = (ruleId: string) => {
    handleUpdateRule(ruleId, {
      condition: {
        field: '',
        operator: '',
        value: ''
      }
    });
  };
  const handleRemoveCondition = (ruleId: string) => {
    handleUpdateRule(ruleId, {
      condition: undefined
    });
  };
  const handleSave = () => {
    // Validate
    if (!tagLabel.trim()) {
      alert('Tag name is required');
      return;
    }
    // Validate rules
    for (const rule of rules) {
      if (!rule.event) {
        alert('Each rule must have an event selected');
        return;
      }
      const eventOption = EVENT_OPTIONS.flatMap(g => g.options).find(o => o.value === rule.event);
      if (eventOption?.needsValue && !rule.eventValue) {
        alert(`Please provide a value for "${eventOption.label}"`);
        return;
      }
    }
    onSave({
      label: tagLabel,
      color: tagColor,
      rules
    });
    onClose();
  };
  const getEventOption = (eventValue: string) => {
    return EVENT_OPTIONS.flatMap(g => g.options).find(o => o.value === eventValue);
  };
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-2961" data-mp="3a7q95h9e2rms">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A] bg-opacity-40" onClick={onClose} data-id="element-2962" data-mp="2mi5g25jiudzs" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[600px] max-h-[85vh] flex flex-col overflow-hidden" data-id="element-2963" data-mp="2igttk8vy28vt">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-[#E2E8F0]" data-id="element-2964" data-mp="2redzhv4r4tcr">
          <div className="flex items-start justify-between" data-id="element-2965" data-mp="bni03g2wb8mv">
            <div data-id="element-2966" data-mp="bq45pa687e2g">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-2967" data-mp="6owybc7zwgwb">
                Edit Tag
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-2968" data-mp="eq5ny30s24pc">
                Configure tag details and automation rules
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-2969" data-mp="ipel7hv10kpn">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-2970" data-mp="no7x55uo03np" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2971" data-mp="3r7jceusvjyx6">
          {/* Section 1: Tag Details */}
          <div data-id="element-2972" data-mp="6ugdk63hg7">
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-4" data-id="element-2973" data-mp="1rn1xmx8qbf">
              Tag Details
            </h3>

            {/* Tag Name */}
            <div className="mb-4" data-id="element-2974" data-mp="4xzyp11wu1du">
              <label className="block text-[14px] font-medium text-[#374151] mb-2" data-id="element-2975" data-mp="9qt31ntgv7pe">
                Tag Name
              </label>
              <Input type="text" value={tagLabel} onChange={e => setTagLabel(e.target.value)} placeholder="Enter tag name" data-id="element-2976" data-mp="38g09sp2rimvo" />
            </div>

            {/* Tag Color */}
            <div data-id="element-2977" data-mp="35wdet8ltmah0">
              <label className="block text-[14px] font-medium text-[#374151] mb-2" data-id="element-2978" data-mp="35uslfmb8sxkq">
                Color
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-2979" data-mp="30uxr18s9dpux">
                {COLOR_PRESETS.map(color => <button key={color.value} onClick={() => setTagColor(color.value)} className={`w-8 h-8 rounded-full transition-all ${tagColor === color.value ? 'ring-2 ring-[#0EA5E9] ring-offset-2' : 'hover:scale-110'}`} style={{
                backgroundColor: color.value
              }} title={color.name} data-id="element-2980" data-mp="2vwctxi8azmap" />)}
              </div>
            </div>
          </div>

          {/* Section 2: Automation Rules */}
          <div data-id="element-2981" data-mp="2d5buyu76nqr5">
            <div className="mb-4" data-id="element-2982" data-mp="288d1mdvcfnu0">
              <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-2983" data-mp="2894nuy31w97m">
                Automation Rules
              </h3>
              <p className="text-[13px] text-[#64748B] mt-1" data-id="element-2984" data-mp="238qp66ncabm9">
                Automatically apply or remove this tag based on events
              </p>
            </div>

            {/* Rules List */}
            {rules.length === 0 ? <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-8 text-center" data-id="element-2985" data-mp="3n29yag7xwdrd">
                <Sparkles className="w-8 h-8 text-[#94A3B8] mx-auto mb-3" data-id="element-2986" data-mp="3s1d3q1ru0lzv" />
                <p className="text-[14px] font-medium text-[#0F172A] mb-1" data-id="element-2987" data-mp="3d5nfdwz6bwrz">
                  No automation rules yet
                </p>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-2988" data-mp="34dv8txd9t7so">
                  Add rules to automatically apply or remove this tag
                </p>
                <Button variant="outline" onClick={handleAddRule} iconLeft={<Plus className="w-4 h-4" data-id="element-2990" data-mp="2ufxmxgivvn38" />} data-id="element-2989" data-mp="395daknn4bcep">
                  Add Rule
                </Button>
              </div> : <div className="space-y-2" data-id="element-2991" data-mp="2ze8xxukx03n1">
                {rules.map(rule => {
              const eventOption = getEventOption(rule.event);
              return <div key={rule.id} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-id="element-2992" data-mp="2lfsq20bbg73b">
                      {/* Delete Rule Button */}
                      <div className="flex justify-end" data-id="element-2993" data-mp="26riolnsf78p1">
                        <button onClick={() => handleDeleteRule(rule.id)} className="p-1 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEE2E2] rounded transition-colors" title="Delete rule" data-id="element-2994" data-mp="26u52s69tgmg8">
                          <Trash2 className="w-4 h-4" data-id="element-2995" data-mp="2fqm5qm811e0w" />
                        </button>
                      </div>

                      {/* Event Selection */}
                      <div data-id="element-2996" data-mp="2ppv8a3gyk0yv">
                        <label className="block text-[12px] text-[#64748B] mb-2" data-id="element-2997" data-mp="2pp1yetmpcvci">
                          When
                        </label>
                        <div className="flex gap-2 items-center" data-id="element-2998" data-mp="2yjzwsg8md0b8">
                          <select value={rule.event} onChange={e => handleUpdateRule(rule.id, {
                      event: e.target.value,
                      eventValue: undefined
                    })} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-2999" data-mp="2ylax262uvpxl">
                            <option value="" data-id="element-3000" data-mp="2neqronaws2x3">Select event...</option>
                            {EVENT_OPTIONS.map(group => <optgroup key={group.group} label={group.group} data-id="element-3001" data-mp="2ijbdtvcwi70o">
                                {group.options.map(option => <option key={option.value} value={option.value} data-id="element-3002" data-mp="172bn3467c08r">
                                    {option.label}
                                  </option>)}
                              </optgroup>)}
                          </select>

                          {/* Dynamic Value Input */}
                          {eventOption?.needsValue && <>
                              <input type={eventOption.valueType === 'currency' ? 'number' : 'number'} value={rule.eventValue || ''} onChange={e => handleUpdateRule(rule.id, {
                        eventValue: e.target.value
                      })} placeholder={eventOption.valueType === 'currency' ? '500' : '3'} className="w-20 h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-3003" data-mp="wyo7csx6eae4" />
                              {eventOption.valueSuffix && <span className="text-[14px] text-[#64748B]" data-id="element-3004" data-mp="1km4metvfk8n4">
                                  {eventOption.valueSuffix}
                                </span>}
                            </>}
                        </div>
                      </div>

                      {/* Condition (Optional) */}
                      {!rule.condition ? <button onClick={() => handleAddCondition(rule.id)} className="text-[13px] text-[#0EA5E9] hover:underline flex items-center gap-1" data-id="element-3005" data-mp="3jcxvafk54t0m">
                          <Plus className="w-3 h-3" data-id="element-3006" data-mp="3eh0q95ebv935" />
                          Add Condition
                        </button> : <div className="bg-white border border-[#E2E8F0] rounded-md p-3 space-y-2" data-id="element-3007" data-mp="3t9kg54cgvk2n">
                          <div className="flex items-center justify-between mb-2" data-id="element-3008" data-mp="3of6ptgcnw34u">
                            <label className="text-[12px] text-[#64748B]" data-id="element-3009" data-mp="654kansidgud">
                              Condition
                            </label>
                            <button onClick={() => handleRemoveCondition(rule.id)} className="text-[#64748B] hover:text-[#EF4444]" data-id="element-3010" data-mp="fxabhvaxru8i">
                              <X className="w-4 h-4" data-id="element-3011" data-mp="ayfqcmphxtl8" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2" data-id="element-3012" data-mp="ovcicrhcph3p">
                            <select value={rule.condition.field} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        field: e.target.value
                      }
                    })} className="h-[36px] pl-2 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '14px 14px'
                    }} data-id="element-3013" data-mp="kxojxegg9672">
                              <option value="" data-id="element-3014" data-mp="1xei2s92dn98">Field...</option>
                              {CONDITION_FIELDS.map(field => <option key={field.value} value={field.value} data-id="element-3015" data-mp="6vzioss8a8y7">
                                  {field.label}
                                </option>)}
                            </select>
                            <select value={rule.condition.operator} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        operator: e.target.value
                      }
                    })} className="h-[36px] pl-2 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] appearance-none bg-no-repeat bg-right" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '14px 14px'
                    }} data-id="element-3016" data-mp="158bvoy2co185">
                              <option value="" data-id="element-3017" data-mp="2jd5hyqjjsasp">Operator...</option>
                              {CONDITION_OPERATORS.map(op => <option key={op.value} value={op.value} data-id="element-3018" data-mp="2o4dsxr0j047z">
                                  {op.label}
                                </option>)}
                            </select>
                            <input type="text" value={rule.condition.value} onChange={e => handleUpdateRule(rule.id, {
                      condition: {
                        ...rule.condition!,
                        value: e.target.value
                      }
                    })} placeholder="Value" className="h-[36px] px-2 border border-[#CBD5E1] rounded-md bg-white text-[13px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-3019" data-mp="1vgrqim31b5mp" />
                          </div>
                        </div>}

                      {/* Action Selection */}
                      <div data-id="element-3020" data-mp="1lq4s8n0y646r">
                        <label className="block text-[12px] text-[#64748B] mb-2" data-id="element-3021" data-mp="1lpd7y8csvsvs">
                          Action
                        </label>
                        <div className="flex gap-2" data-id="element-3022" data-mp="1gt620fkemhdu">
                          <button onClick={() => handleUpdateRule(rule.id, {
                      action: 'apply'
                    })} className={`flex-1 h-[36px] px-4 rounded-md text-[14px] font-medium transition-all ${rule.action === 'apply' ? 'bg-[#0EA5E9] text-white' : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-3023" data-mp="1ur31bjq8t1w7">
                            Apply Tag
                          </button>
                          <button onClick={() => handleUpdateRule(rule.id, {
                      action: 'remove'
                    })} className={`flex-1 h-[36px] px-4 rounded-md text-[14px] font-medium transition-all ${rule.action === 'remove' ? 'bg-[#64748B] text-white' : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-3024" data-mp="1btjn81oxgt2x">
                            Remove Tag
                          </button>
                        </div>
                      </div>
                    </div>;
            })}

                {/* Add Rule Button */}
                <button onClick={handleAddRule} className="w-full h-[40px] border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] text-[#0EA5E9] hover:border-[#0EA5E9] hover:bg-[#F0F9FF] transition-all flex items-center justify-center gap-2" data-id="element-3025" data-mp="2h01dtdqejeo6">
                  <Plus className="w-4 h-4" data-id="element-3026" data-mp="39lt3hrmrzrgg" />
                  Add Rule
                </button>
              </div>}

            {/* Examples */}
            <div className="mt-4" data-id="element-3027" data-mp="2zqj47bnrhy6d">
              <button onClick={() => setShowExamples(!showExamples)} className="text-[13px] text-[#0EA5E9] hover:underline flex items-center gap-1" data-id="element-3028" data-mp="2zp83jkpm56jh">
                {showExamples ? 'Hide examples' : 'See examples'}
                <ChevronDown className={`w-4 h-4 transition-transform ${showExamples ? 'rotate-180' : ''}`} data-id="element-3029" data-mp="1ocnpg40rrrfo" />
              </button>
              {showExamples && <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md space-y-2" data-id="element-3030" data-mp="2kxigla6brq20">
                  <p className="text-[13px] text-[#64748B]" data-id="element-3031" data-mp="2fxw26xerg3rm">
                    • When Payment failed + Retry count &gt; 2 → Apply tag
                  </p>
                  <p className="text-[13px] text-[#64748B]" data-id="element-3032" data-mp="2b0vw4mhwv10i">
                    • When Payment recovered → Remove tag
                  </p>
                  <p className="text-[13px] text-[#64748B]" data-id="element-3033" data-mp="33mot2fgizlfd">
                    • When Orders completed = 3 → Apply tag
                  </p>
                </div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#E2E8F0] bg-[#F8FAFC] p-4" data-id="element-3034" data-mp="2tw3yr47vx329">
          <div className="flex items-center justify-between" data-id="element-3035" data-mp="2oy2c69b92lm1">
            {onDelete && <button onClick={onDelete} className="text-[14px] text-[#EF4444] hover:underline" data-id="element-3036" data-mp="3mehla7nrt2m1">
                Delete Tag
              </button>}
            <div className={`flex gap-3 ${!onDelete ? 'ml-auto' : ''}`} data-id="element-3037" data-mp="3hlepdsvith2o">
              <Button variant="outline" onClick={onClose} data-id="element-3038" data-mp="3pxgooo8hiaw8">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} data-id="element-3039" data-mp="3ty909o4twrvo">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}